import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  useChannels,
  useChat,
  useAgents,
  useSecretChat,
  useGame,
  ChannelList,
  ChannelDetailsModal,
  MemberList,
  MessageList,
  MessageForm,
  SecretChatPanel,
} from 'features/chat';

// Selected-channel state lives in the URL (/channels/:channelId), not in component
// state: this component only derives what to render from `channelId` and reacts to
// it changing, so switching channels, reloading, and opening a direct link all
// behave the same way.
const ChatPage = () => {
  const { me } = useOutletContext();
  const { channelId } = useParams();
  const navigate = useNavigate();
  const {
    channels,
    loading: channelsLoading,
    error: channelsError,
    addChannel,
    removeChannel,
  } = useChannels();
  const [showChannelMenu, setShowChannelMenu] = useState(false);
  const [showChannelDetails, setShowChannelDetails] = useState(false);
  const channelMenuRef = useRef(null);

  const selectedChannel = channels.find((channel) => channel.id === channelId);
  // Only meaningful once the channel list has finished loading: a channelId that
  // does not (or no longer) match any active channel.
  const channelNotFound = !channelsLoading && Boolean(channelId) && !selectedChannel;

  // "/" (no channelId in the URL) redirects to the first channel once the list has loaded.
  useEffect(() => {
    if (!channelsLoading && !channelId && channels.length > 0) {
      navigate(`/channels/${channels[0].id}`, { replace: true });
    }
  }, [channelsLoading, channelId, channels, navigate]);

  // Close the channel menu when clicking outside of it.
  useEffect(() => {
    if (!showChannelMenu) return undefined;
    const onDocClick = (e) => {
      if (channelMenuRef.current && !channelMenuRef.current.contains(e.target)) {
        setShowChannelMenu(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showChannelMenu]);

  const {
    messages,
    members,
    secretMessages,
    loading: messagesLoading,
    error: chatError,
    connected,
    sendMessage,
    sendSecretMessage,
  } = useChat(channelNotFound ? null : channelId);

  const {
    agents,
    error: agentsError,
    createAgent,
    deleteAgent,
  } = useAgents(channelNotFound ? null : channelId);

  const {
    messages: secretChatMessages,
    loading: secretChatLoading,
    error: secretChatError,
  } = useSecretChat(channelNotFound ? null : channelId, secretMessages, sendSecretMessage);

  const { game, starting: gameStarting, error: gameError, start: startGame } = useGame(channelNotFound ? null : channelId);

  const gameMasterAgent = agents.find((agent) => agent.isGameMaster);

  // Once we know the channelId is bad, the dedicated "Channel not found" banner
  // below already explains it, so the (likely 404-flavored) chat/agents errors are redundant.
  const error = channelsError || (!channelNotFound && (chatError || agentsError || secretChatError || gameError));

  const handleStartGame = async () => {
    try {
      await startGame();
    } catch (e) {
      // Surfaced via gameError above; nothing further to do here.
    }
  };

  const handleSelectChannel = (id) => {
    navigate(`/channels/${id}`);
  };

  const handleOpenChannelDetails = () => {
    setShowChannelMenu(false);
    setShowChannelDetails(true);
  };

  const handleDeleteChannel = async (id) => {
    await removeChannel(id);
    if (id === channelId) {
      navigate('/', { replace: true });
    }
  };

  const showEmptyState = !channelId || channelNotFound;

  return (
    <div className="gh-chat-layout">
      <ChannelList
        channels={channels}
        selectedChannelId={channelId}
        onSelect={handleSelectChannel}
        onCreate={addChannel}
      />

      <div className="gh-chat-main-split">
        <div className="gh-box gh-chat-main">
          <div className="gh-box-header d-flex align-items-center justify-content-between">
            <span>{selectedChannel ? `# ${selectedChannel.name}` : 'Select a channel'}</span>
            {selectedChannel && (
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-gh-primary"
                  onClick={handleStartGame}
                  disabled={showEmptyState || gameStarting || (game && game.inProgress)}
                >
                  {gameStarting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Starting...
                    </>
                  ) : game && game.inProgress ? (
                    'Game in progress'
                  ) : (
                    <>
                      <i className="bi bi-play-fill me-1" aria-hidden="true"></i>
                      Start Game
                    </>
                  )}
                </button>
                <div className="position-relative" ref={channelMenuRef}>
                  <button
                    type="button"
                    className="gh-channel-menu-btn"
                    onClick={() => setShowChannelMenu((prev) => !prev)}
                    aria-label="Channel menu"
                    aria-expanded={showChannelMenu}
                  >
                    <i className="bi bi-three-dots-vertical" aria-hidden="true"></i>
                  </button>
                  <ul
                    className={`dropdown-menu dropdown-menu-end mt-1${showChannelMenu ? ' show' : ''}`}
                    style={{ top: '100%', right: 0 }}
                  >
                    <li>
                      <button
                        type="button"
                        className="dropdown-item d-flex align-items-center"
                        onClick={handleOpenChannelDetails}
                      >
                        <i className="bi bi-info-circle me-2" aria-hidden="true"></i>
                        Channel details
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="px-3 pt-3">
              <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
                <i className="bi bi-exclamation-octagon-fill" aria-hidden="true"></i>
                {error}
              </div>
            </div>
          )}

          {channelNotFound && (
            <div className="px-3 pt-3">
              <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2" role="alert">
                <span className="d-flex align-items-center gap-2">
                  <i className="bi bi-exclamation-octagon-fill" aria-hidden="true"></i>
                  Channel not found
                </span>
                {channels.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-gh"
                    onClick={() => navigate(`/channels/${channels[0].id}`, { replace: true })}
                  >
                    Go to # {channels[0].name}
                  </button>
                )}
              </div>
            </div>
          )}

          {showEmptyState ? (
            <div className="gh-box-body text-center text-muted-gh">
              {channelsLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading...
                </>
              ) : channels.length === 0 ? (
                'No channels yet. Create one to get started.'
              ) : (
                'Select a channel'
              )}
            </div>
          ) : messagesLoading ? (
            <div className="gh-box-body d-flex align-items-center gap-2 text-muted-gh">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Loading...
            </div>
          ) : (
            <MessageList messages={messages} currentUserId={me && me.id} members={members} agents={agents} />
          )}

          <div className="gh-box-footer">
            <MessageForm
              onSend={sendMessage}
              disabled={!connected || showEmptyState}
              members={members}
              agents={agents}
            />
          </div>
        </div>

        {!showEmptyState && (
          <SecretChatPanel
            messages={secretChatMessages}
            currentUserId={me && me.id}
            gameMasterAgent={gameMasterAgent}
            connected={connected}
            onSend={sendSecretMessage}
            loading={secretChatLoading}
          />
        )}
      </div>

      <MemberList
        members={members}
        agents={agents}
        onAddAgent={createAgent}
        onRemoveAgent={deleteAgent}
        menuDisabled={showEmptyState}
      />

      {showChannelDetails && selectedChannel && (
        <ChannelDetailsModal
          channel={selectedChannel}
          memberCount={members.length}
          onClose={() => setShowChannelDetails(false)}
          onDelete={handleDeleteChannel}
        />
      )}
    </div>
  );
};

export default ChatPage;
